// @flow

import React, { Component } from "react"
import { connect } from "react-redux"
import { getJobs } from "../../actions"
import BigList from "../../components/BigList"
import ArrowIcon from "../../components/ArrowIcon"
import LabeledValue from "../../components/LabeledValue"
import FileUpload from "../../components/FileUpload"
import SocketConnection from "../../components/Socket"
import styles from "./styles.css"
import moment from "moment"
import ptLocale from "moment/locale/pt"
import _ from "lodash"
import * as strings from "../../strings"
import ProgressBar from "react-toolbox/lib/progress_bar"
import Dropdown from "react-toolbox/lib/dropdown"
import { formatDateTime } from "../../utils"

const STATUS_TRANSLATION = {
  "Done": strings.generalStrings.complete,
  "Error": strings.generalStrings.error,
  "Executing": strings.processes.inExecution,
  "Waiting": strings.processes.waitingExecution,
  "Scheduled": strings.processes.waitingExecution,
  "Stalled": strings.processes.stopped,
}

const JOB_TYPE_TRANSLATION = {
  "PeriodicCheck": strings.jobs.periodicCheck,
  "CitiusJob": strings.jobs.citius,
  "ImportJob": strings.imports._import,
  "CttFtp": strings.imports.uploadCtt,
  "ExecutionListUpdate": strings.jobs.executionListUpdate,
}

const JOB_IMPORT_TYPE_TRANSLATION = {
  "ImportEdpInjunction": strings.imports.edpInjunction,
  "ImportEdpPreLitigation": strings.imports.edpPreLitigation,
}

const IMPORT_TYPES = [
  { value: "EdpInjunction", label: strings.imports.edpTag },
  { value: "PtCarteiras", label: strings.imports.outrosTag },
  { value: "EdpPreLitigation", label: strings.imports.edpPreLitigation}
]

const COLUMNS = [ {
  title: strings.generalStrings.id,
  key: "id",
  align: "left",
}, {
  title: strings.generalStrings.type,
  key: "type",
  align: "left",
  transform: x => JOB_TYPE_TRANSLATION[ x ] || x,
}, {
  title: strings.generalStrings.status,
  key: "status",
  align: "left",
  transform: x => STATUS_TRANSLATION[ x ] || x,
}, {
  title: strings.generalStrings.addedOn,
  key: "queueTime",
  align: "left",
  transform: formatDateTime,
} ]

// Number of jobs to fetch by default
const DEFAULT_JOB_FETCH_SIZE = 25


class Jobs extends Component {
  props: {
    jobs: any,
    getJobs: () => any,
  }

  state = {
    expandedJobs: [],
    selectedImportType: "",
  }

  componentWillMount () {
    this.props.getJobs( DEFAULT_JOB_FETCH_SIZE )
  }

  translateStatus = status => STATUS_TRANSLATION[ status ]

  prepareDate = date => moment( date ).locale( strings.generalStrings.languageTag, ptLocale ).format( "LLL" )

  prepareData = instance => {
    let ommited = _.omit( instance, [ "data", "logId" ] )
    return {
      ...ommited,
      queueTime: this.prepareDate( ommited.queueTime ),
      executionTime: this.prepareDate( ommited.executionTime ),
      status: this.translateStatus( ommited.status ),
    }
  }

  renderProgressBar = ( enabled, value ) => enabled ? (
    <ProgressBar
      className={styles.progressBar}
      type="linear"
      mode="determinate"
      value={value}
    />
  ) : (
    <div style={{ minHeight: "4px" }} />
  )

  rowOnClick = ( jobId, isSelected ) => () => {
    const expandedJobs = this.state.expandedJobs

    const newExpandedJobs = isSelected ?
      _.pull( expandedJobs, jobId ) :
      [ ...expandedJobs, jobId ]

    this.setState( {
      expandedJobs: newExpandedJobs,
    } )
  }

  renderRowHeader = ( rowIndex, isRowOpen, item, columns ) => {
    return (
      <div key={rowIndex} onClick={this.rowOnClick( item.id, isRowOpen )} className={styles.itemColumnWrapper}>
        {columns.map( column => (
          <div key={column.key} className={styles.item}>
            {
              column.transform( item[ column.key ], column.key, item ) || "--"
            }
          </div>
        ) )}
        <ArrowIcon
          onClick={this.rowOnClick( rowIndex, isRowOpen )}
          invert={isRowOpen}
          iconName="keyboard_arrow_up"
        />
      </div>
    )
  }

  renderRowBody = ( isRowOpen, item ) => {
    if ( !isRowOpen ) {
      return null
    }

    return (
      <div
        className={styles.rowBody}
        style={{
          maxHeight: isRowOpen ? undefined : "0px",
        }}
      >
        {this.renderJobSpecificInfo( item )}
        {this.renderJobLog( item )}
      </div>
    )
  }

  renderJobSpecificInfo = ( job ) => {
    const jobData = JSON.parse( job.data || "{}" )
    return (
      <div className={styles.jobInfoRow}>
        <LabeledValue label={strings.generalStrings.executedOn}>
          {( job.executionTime && formatDateTime( job.executionTime ) ) || "--"}
        </LabeledValue>
        {job.type === "ImportJob" ? this.renderImportJobInfo( jobData, job ) : undefined}
      </div>
    )
  }

  renderImportJobInfo = ( jobData ) => {
    const importType = jobData.scheduleData && jobData.scheduleData.importType
    return (
      <div>
        <LabeledValue label={strings.imports.importType}>
          {JOB_IMPORT_TYPE_TRANSLATION[ importType ] || importType}
        </LabeledValue>
      </div>
    )
  }

  renderJobLog = ( job ) => {
    if ( !job.log ) {
      return null
    }

    return (
      <LabeledValue label="Output">
        <div className={styles.jobLogWrapper}>
          {job.log}
        </div>
      </LabeledValue>
    )
  }

  renderItems ( columns, items ) {
    return (
      <div className={styles.items}>
        {items.map( ( item, rowIndex ) => {
          const isRowOpen = _.includes( this.state.expandedJobs, item.id )
          return (
            <div key={rowIndex} className={styles.itemWrapper}>
              {
                // TODO: this true should be, if the job is being executed
                this.renderProgressBar( item.status === "Executing", item.progress || 0 )
              }
              {this.renderRowHeader( rowIndex, isRowOpen, item, columns )}
              {this.renderRowBody( isRowOpen, item )}
            </div>
          )
        } )}
      </div>
    )
  }

  handleImportTypeChange = selectedImportType => this.setState( { selectedImportType } )

  render () {
    const jobs = this.props.jobs || []
    const sortedJobs = _.sortBy( jobs, "id" ).reverse()
    return (
      <div className={styles.root}>
        <div className={styles.importWrapper}>
          <div className={styles.importBlock}>
            <Dropdown
              auto={false}
              source={IMPORT_TYPES}
              label="Tipo de importação"
              value={this.state.selectedImportType}
              onChange={this.handleImportTypeChange}
            />
            <FileUpload
              command="newImport"
              args={{
                ft: this.state.selectedImportType,
              }}
              onCompleteOk={() => {}}
            />
          </div>
        </div>
        <BigList
          loadMore={() => {}}
          hasMoreItems={() => false}
          columns={COLUMNS}
          items={sortedJobs}

          renderItems={this.renderItems.bind( this )}
        />
        <SocketConnection />
      </div>
    )
  }
}

const mapStateToProps = ( store ) => {
  return {
    jobs: store.jobs,
  }
}

export default connect( mapStateToProps, { getJobs } )( Jobs )



// WEBPACK FOOTER //
// ./src/views/Jobs/index.jsx